import {
  lunchMealToCartId,
  lunchMealsOn,
  lunchOrderableDates,
  tt,
  zonedWallTime,
  zonedWallTimeToUtcMs,
  type LunchWeek,
} from '@agos/shared'

import { cutoffOf, type OpeningHours } from '@agos/customer-core'

/**
 * Ordering for later: the wall time the customer picks, and whether the server will accept it.
 *
 * The conversion itself — a Bratislava wall time to an instant, across a clock change — lives in
 * `@agos/shared`, beside the formatters that pin the same zone. FB-C09 and its measurement are
 * documented there; what is left here is the checkout's own policy.
 */

/** What goes in `scheduledAt` — an instant the server can parse, or `null`. */
export function scheduledAtOf(date: string, time: string): string | null {
  const instant = zonedWallTimeToUtcMs(date, time)
  return Number.isNaN(instant) ? null : new Date(instant).toISOString()
}

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const

function minutesOf(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
  if (match === null) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 23 || minutes > 59) return null
  return hours * 60 + minutes
}

/**
 * Why this time will be refused, in the server's own terms, or `''` when it will not be.
 *
 * A mirror of `RestaurantAvailability.requireWithinOpeningHours`, including the overnight case, which is
 * `!closes.isAfter(opens)` there and the kind of branch a rewrite drops. The point is that the customer
 * learns before submitting rather than by being turned away.
 */
export function preorderProblem(
  date: string,
  time: string,
  hours: OpeningHours | null,
  now: Date,
): string {
  const instant = zonedWallTimeToUtcMs(date, time)
  if (Number.isNaN(instant)) return tt('Vyberte dátum aj čas.')
  if (instant <= now.getTime()) return tt('Zvolený čas už bol.')

  // Unreadable hours are not a refusal here: the server's own check gives up quietly when it cannot
  // read them, and refusing on this side would be stricter than the rule being mirrored.
  if (hours === null) return ''
  if (hours.emergencyClosed === true) return tt('Reštaurácia je dočasne zatvorená.')

  const wall = zonedWallTime(instant)
  const day = hours.days?.[DAY_KEYS[wall.day] ?? '']
  if (day === undefined) return ''
  if (day.open !== true) return tt('Reštaurácia je v zvolený deň zatvorená - vyberte iný čas.')

  const opens = minutesOf(day.from)
  const closes = minutesOf(cutoffOf(day))
  if (opens === null || closes === null) return ''

  const overnight = closes <= opens
  const inside = overnight
    ? wall.minutes >= opens || wall.minutes <= closes
    : wall.minutes >= opens && wall.minutes <= closes
  if (!inside) {
    return `${tt('Čas doručenia musí byť v rámci otváracích hodín')} (${day.from} – ${cutoffOf(day)}).`
  }
  return ''
}

/**
 * The day a basket's daily-menu lines are for - FRONTEND.md §9.219, corrected by §9.649 (owner O35).
 *
 * *"For ordering for another day except today, Časenka should be enabled by default and also should
 * be available only for that specific date and time."*
 *
 * **Read off the basket, not remembered from the menu screen.** A meal is added from a day and then
 * the customer walks away, adds a pizza and comes back; reading the lines at the checkout gives the
 * right answer to somebody who emptied and refilled their basket.
 *
 * **And resolved against the dates the menu offered, which is §9.649's fix.** This used to walk the weeks and
 * take `dayDateOf(week.startDate, day.dayKey)`, so it read a meal's date off the week's stored stamp while the
 * menu showed that same week at *this* week's dates under §9.399's repeat. Measured on the owner's rows: their
 * three weeks are stamped 2026-09-14, -21 and -28, so every lunch line resolved to a date already gone, the
 * `date <= today` arm dropped it, **no `Časenka` was pinned at all**, and the order went in for today - which
 * on a Saturday is a day Šurany serves no lunch. Both halves of their report, one cause.
 *
 * **The nearest offered date per meal, the furthest of those across the basket.** A repeating week puts the
 * same meal id under several tabs, so *which day is this line for* has no answer from the basket alone - the
 * nearest is the one the customer was looking at, and today's lunch therefore still pins nothing. Across the
 * basket the furthest wins, because a basket cannot be delivered twice and the later day is the one that can
 * carry both; refusing it would punish a customer for a combination the menu itself offered.
 *
 * **Today is not a preorder** — a lunch taken for today is an ordinary order, and turning *Časenka*
 * on for it would make somebody pick a time to receive food they are asking for now.
 */
export function lunchPreorderDate(
  lines: readonly { productId?: number; lunchDate?: string }[],
  weeks: readonly LunchWeek[],
  today: string,
): string | null {
  if (lines.length === 0) return null
  const offered = lunchOrderableDates(weeks, today)
  let furthest: string | null = null
  const consider = (date: string | undefined) => {
    if (date === undefined || date <= today) return
    if (furthest === null || date > furthest) furthest = date
  }

  for (const line of lines) {
    if (line.productId === undefined) continue
    // §9.710, FB-1014 - **the day the line was picked for, where it says.** This used to take the first offered date
    // carrying the meal, reasoning that a later tab with the same meal is *the repeat, not a second choice* - and a
    // repeating week serves the same meals on this Tuesday and the next, so next Tuesday's lunch read as today's and
    // no časenka was forced. A dated line answers for itself; only an undated one, saved before, is derived.
    if (line.lunchDate !== undefined) {
      consider(line.lunchDate)
      continue
    }
    const id = line.productId
    consider(offered.find((date) => lunchMealsOn(weeks, date).some((meal) => lunchMealToCartId(meal) === id)))
  }

  return furthest
}

/**
 * The window a preorder for a daily menu may be placed in — §9.219.
 *
 * The menu's own serving hours, not the shop's: a *Časenka* for 21:00 on a menu served 11:00–14:00
 * is an order the kitchen will not honour. Empty settings answer null rather than a made-up window.
 */
export function lunchServingWindow(
  settings: { timeFrom?: string; timeTo?: string } | null | undefined,
): { from: string; to: string } | null {
  const from = (settings?.timeFrom ?? '').trim()
  const to = (settings?.timeTo ?? '').trim()
  if (from === '' || to === '' || from >= to) return null
  return { from, to }
}

/** What the checkout holds for *Objednať na neskôr / Časenka*: off (`null`), or a date and a time. */
export interface Preorder {
  date: string
  time: string
}

/**
 * The časenka the checkout must hold for this basket - FRONTEND.md §9.709, owner O92, decision #131.
 *
 * *"Obedové Menu in customer website is not making 'Objednať na neskôr / Časenka' as mandator. Make sure it
 * will make it mandator for that specific day!!!"* §9.219 applied the časenka as *a default and not a lock*, so
 * a customer could switch it off and the future day's menu went out as an immediate order, for today. Owner
 * decision #131: for a future day it is a lock.
 *
 * **No lunch day, the customer's own choice, untouched.** That is today's menu (`lunchPreorderDate` answers
 * null for it, and today may still go out as soon as possible) and it is also the release: removing the lunch
 * meals from the basket makes the switch and the date the customer's again.
 *
 * **A lunch day, a časenka on exactly that day.** Switched off or moved to another date by any route, it comes
 * back on the lunch day. The time the customer chose is kept - a time outside the serving window is
 * `lunchPreorderProblem`'s to say, not this function's to overwrite - and a časenka that was off starts with no
 * time at all, because a pre-filled 11:00 is a delivery time nobody chose (legacy starts it empty too).
 *
 * **The same object once it is satisfied.** `CheckoutPage` applies this during render, comparing by identity,
 * so the correction settles in one pass instead of setting state on every render.
 */
export function lunchPreorderEnforced(
  preorder: Preorder | null,
  lunchDay: string | null,
): Preorder | null {
  if (lunchDay === null) return preorder
  if (preorder !== null && preorder.date === lunchDay) return preorder
  return { date: lunchDay, time: preorder?.time ?? '' }
}

/** `2026-10-07` -> `7. 10. 2026`, read off the string so no time zone can move the day. */
export function lunchDayText(date: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  if (match === null) return date
  return `${Number(match[3])}. ${Number(match[2])}. ${match[1]}`
}

/**
 * Why the časenka does not satisfy the basket's lunch day, or `''` when it does - §9.709.
 *
 * The four refusals legacy's checkout makes at submit (`Checkout.tsx`'s `lunchInfo` guard), in the order a
 * customer would meet them: switched off, on another date, no time chosen, a time outside the hours the menu is
 * served. **Answered before the submit rather than at it**, so `maySubmit` and `submitBlock` carry it beside the
 * button through `schedulingProblem` - the button is disabled with the reason, never pressed into a refusal.
 *
 * The first two cannot arise while `lunchPreorderEnforced` is applied; they are the defensive half, so the
 * submit is refused rather than sent if anything ever undoes the lock.
 */
export function lunchPreorderProblem(
  preorder: Preorder | null,
  lunchDay: string | null,
  window: { from: string; to: string } | null,
): string {
  if (lunchDay === null) return ''
  if (preorder === null) return tt('Pri obedovom menu na iný deň je časenka povinná.')
  if (preorder.date !== lunchDay) {
    return `${tt('Časenka musí byť na')} ${lunchDayText(lunchDay)} ${tt('kvôli obedovému menu.')}`
  }
  const chosen = minutesOf(preorder.time)
  if (chosen === null) {
    return window === null
      ? tt('Vyberte čas doručenia obedového menu.')
      : `${tt('Vyberte čas doručenia obedového menu')} (${window.from} – ${window.to}).`
  }
  if (window === null) return ''
  const from = minutesOf(window.from)
  const to = minutesOf(window.to)
  if (from === null || to === null) return ''
  if (chosen < from || chosen > to) {
    return `${tt('Čas doručenia musí byť v rámci obedových hodín')} (${window.from} – ${window.to}).`
  }
  return ''
}

/**
 * The serving window's quarter-hours, `from` to `to` inclusive - the quick-pick chips under the time - §9.709.
 *
 * Legacy draws the same chips (`Checkout.tsx`, *Quick-pick time chips*), starting at the first quarter-hour at or
 * after `from`. Never today's - a lunch day is always a future one - so there is no *now + 30 minutes* floor to
 * apply. No window, no chips: inventing one is what `lunchServingWindow` already refuses to do.
 */
export function lunchSlots(window: { from: string; to: string } | null): string[] {
  if (window === null) return []
  const from = minutesOf(window.from)
  const to = minutesOf(window.to)
  if (from === null || to === null) return []
  const slots: string[] = []
  for (let minute = Math.ceil(from / 15) * 15; minute <= to; minute += 15) {
    const hours = String(Math.floor(minute / 60)).padStart(2, '0')
    slots.push(`${hours}:${String(minute % 60).padStart(2, '0')}`)
  }
  return slots
}

/**
 * The one sentence the checkout's `scheduling` carries - §9.709.
 *
 * The lunch day first, because a časenka on the wrong day or with no time is the reason that has to be fixed
 * before the opening hours can be asked about at all; then `preorderProblem`, which mirrors the server's own
 * opening-hours check. `maySubmit` already requires this to be `''`, and `submitBlock` already says it beside
 * the button, so the lunch refusal reaches both without a new term in either.
 */
export function schedulingProblem(input: {
  preorder: Preorder | null
  lunchDay: string | null
  lunchWindow: { from: string; to: string } | null
  hours: OpeningHours | null
  now: Date
}): string {
  const lunch = lunchPreorderProblem(input.preorder, input.lunchDay, input.lunchWindow)
  if (lunch !== '') return lunch
  if (input.preorder === null) return ''
  return preorderProblem(input.preorder.date, input.preorder.time, input.hours, input.now)
}
